/**
 * Light/dark theme state machine, shared by both Lucent SPAs.
 *
 * Lifted from the identical implementations that lived in
 * `dashboard-app/src/App.vue` and `insights-admin/src/App.vue` — same
 * `lucent-theme` storage key, same precedence, same swallowed-error policy.
 * Both are now thin callers.
 *
 * Precedence, in order:
 *   1. An explicit stored preference in `localStorage` wins outright.
 *   2. Otherwise the system `prefers-color-scheme` sets the initial value
 *      **and keeps tracking live changes** — so a user who has never touched
 *      the toggle follows their OS when it flips at sunset.
 *   3. Once the toggle is clicked, `userPicked` latches and OS tracking stops.
 *      Following the OS after an explicit choice would silently override it.
 *
 * The theme is applied as `data-theme` on `<html>` rather than a class,
 * because `tokens.css` scopes its dark overrides with
 * `html[data-theme="dark"]` and the embedded `<lucent-insight>` runtime reads
 * the same attribute.
 *
 * Every `localStorage` access is wrapped: private-browsing modes and
 * storage-disabled configurations throw on access rather than returning null,
 * and losing persistence is not worth breaking the shell over.
 */

import { onBeforeUnmount, onMounted, ref, type Ref } from "vue";

export type Theme = "light" | "dark";

/** Shared across both apps deliberately — one preference for one user. */
const STORAGE_KEY = "lucent-theme";

export interface UseThemeResult {
  /** The active theme. Read-only in practice; mutate via `toggleTheme`. */
  theme: Ref<Theme>;
  /** Flip the theme and latch the choice, stopping OS-preference tracking. */
  toggleTheme: () => void;
}

/**
 * Install the theme state machine. Call once, from the app shell.
 *
 * Registers an `onMounted` hook that resolves the initial value and subscribes
 * to OS changes, and an `onBeforeUnmount` hook that unsubscribes — so this must
 * be called during component setup, not from arbitrary module scope.
 */
export const useTheme = (): UseThemeResult => {
  const theme = ref<Theme>("light");
  // Latches on the first explicit toggle; from then on the OS is ignored.
  const userPicked = ref(false);

  const applyTheme = (next: Theme): void => {
    theme.value = next;
    document.documentElement.setAttribute("data-theme", next);
  };

  const toggleTheme = (): void => {
    applyTheme(theme.value === "dark" ? "light" : "dark");
    userPicked.value = true;
    try {
      localStorage.setItem(STORAGE_KEY, theme.value);
    } catch {
      /* private mode / disabled storage — soldier on without persistence */
    }
  };

  let mql: MediaQueryList | null = null;
  const onSystemThemeChange = (e: MediaQueryListEvent): void => {
    if (!userPicked.value) applyTheme(e.matches ? "dark" : "light");
  };

  onMounted(() => {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch {
      /* ignore — fall through to the system preference */
    }
    if (stored === "dark" || stored === "light") {
      userPicked.value = true;
      applyTheme(stored);
    } else {
      const prefersDark =
        window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches;
      applyTheme(prefersDark ? "dark" : "light");
    }
    mql = window.matchMedia?.("(prefers-color-scheme: dark)") ?? null;
    mql?.addEventListener("change", onSystemThemeChange);
  });

  onBeforeUnmount(() => {
    mql?.removeEventListener("change", onSystemThemeChange);
  });

  return { theme, toggleTheme };
};
